/**
 * Accept Invitation — navy+gold landing page.
 *
 * Shown when an agent clicks the activation link in their invite email.
 * Phase 1: Validates the token (does NOT consume it) and shows invitation details.
 * Phase 2: Agent clicks Sign In or Create Account — token is passed through
 *          so the signup/login flow can call accept-invitation to activate.
 *
 * Route: /accept-invitation?token=...  (also /activate/:token legacy path)
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const colors = {
  navy:  '#0F1E35',
  navy2: '#162844',
  navy3: '#1E3557',
  gold:  '#C9952A',
  gold2: '#E8B44A',
  gold3: '#F5D080',
  white: '#FFFFFF',
  off:   '#E8EEF6',
  muted: '#8FA3BF',
  green: '#27ae60',
  red:   '#e74c3c',
};

const fonts = {
  display: "'Bebas Neue', Impact, 'Arial Black', sans-serif",
  body:    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
};

const FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';

interface InvitationDetails {
  brokerageName: string;
  brokerageId: string;
  agentEmail: string;
  agentFirstName: string;
  agentLastName: string;
  plan: string;
  fundingModel: string;
  sponsorSubsidyPct: number;
  agentMonthlyFee: number;
  isSponsorPaid: boolean;
  userExists: boolean;
}

const planLabels: Record<string, { name: string; price: string; features: string[] }> = {
  essential: {
    name: 'Agent Hub',
    price: 'Free',
    features: ['CRM & Lead Management', 'Team Chat', 'Document Management'],
  },
  professional: {
    name: 'AI Search',
    price: '$135/mo',
    features: ['Everything in Agent Hub', 'AI Buyer Search Portal', 'Smart CMA Reports', 'Gmail/Outlook Integration'],
  },
  premium: {
    name: 'AI Pro',
    price: '$185/mo',
    features: ['Everything in AI Search', 'AI Lead Coach', 'County Intelligence', 'Automated Follow-ups'],
  },
  deal_ai: {
    name: 'Deal AI',
    price: '$350/mo',
    features: ['Everything in AI Pro', 'Transaction Management', 'Deal Pipeline', 'Commission Tracking'],
  },
};

const AcceptInvitationAIForward: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [alreadyActive, setAlreadyActive] = useState(false);

  const token = searchParams.get('token') || pathToken;

  useEffect(() => {
    validateInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateInvitation = async () => {
    if (!token) {
      setLoading(false);
      setError('No invitation token provided. Please use the link from your email.');
      return;
    }
    try {
      const response = await axios.post(
        '/api/broker/invitations/validate-invitation',
        { token },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data?.alreadyActive) {
        setAlreadyActive(true);
      }
      if (response.data?.invitation) {
        setInvitation(response.data.invitation);
      } else if (response.data?.brokerageName) {
        // Already active — minimal info
        setInvitation({
          brokerageName: response.data.brokerageName,
          brokerageId: '',
          agentEmail: '',
          agentFirstName: '',
          agentLastName: '',
          plan: 'essential',
          fundingModel: 'full_sponsor',
          sponsorSubsidyPct: 100,
          agentMonthlyFee: 0,
          isSponsorPaid: true,
          userExists: true,
        });
      }
    } catch (err: any) {
      console.error('Error validating invitation:', err);
      setError(
        err?.response?.data?.error ||
          'Your invitation link is invalid or has expired. Please ask your broker to resend it.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    navigate(`/agent-portal/login?invite_token=${token}`);
  };

  const handleSignUp = () => {
    const params = new URLSearchParams();
    params.set('invite_token', token || '');
    if (invitation) {
      params.set('bid', invitation.brokerageId);
      params.set('sponsor', invitation.brokerageName);
      params.set('plan', invitation.plan);
      if (invitation.agentEmail) params.set('email', invitation.agentEmail);
    }
    navigate(`/agent-portal/signup?${params.toString()}`);
  };

  // ---- Loading state -------------------------------------------------------
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: colors.navy, fontFamily: fonts.body, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <link href={FONT_LINK} rel="stylesheet" />
        <div style={{ textAlign: 'center', color: colors.off }}>
          <div style={{ width: '60px', height: '60px', margin: '0 auto 20px auto', border: `3px solid ${colors.navy3}`, borderTopColor: colors.gold, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <div style={{ fontFamily: fonts.display, fontSize: '20px', letterSpacing: '2px', color: colors.white }}>
            Verifying your invitation…
          </div>
          <div style={{ fontSize: '13px', color: colors.muted, marginTop: '8px' }}>One moment while we check your details</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ---- Error state ---------------------------------------------------------
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: colors.navy, fontFamily: fonts.body, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <link href={FONT_LINK} rel="stylesheet" />
        <div style={{ maxWidth: '520px', background: colors.navy2, border: `1px solid ${colors.navy3}`, borderTop: `4px solid ${colors.red}`, borderRadius: '12px', padding: '40px 32px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
          <div style={{ fontFamily: fonts.display, fontSize: '28px', letterSpacing: '1.5px', color: colors.white, marginBottom: '12px' }}>
            Invitation Issue
          </div>
          <div style={{ fontSize: '14px', color: colors.off, lineHeight: 1.6, marginBottom: '24px' }}>{error}</div>
          <button
            onClick={() => navigate('/agent-portal/login')}
            style={{ background: colors.gold, color: colors.navy, border: 'none', borderRadius: '6px', padding: '12px 28px', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer', fontFamily: fonts.body }}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  // ---- Already active state ------------------------------------------------
  if (alreadyActive) {
    return (
      <div style={{ minHeight: '100vh', background: colors.navy, fontFamily: fonts.body, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <link href={FONT_LINK} rel="stylesheet" />
        <div style={{ maxWidth: '520px', background: colors.navy2, border: `1px solid ${colors.navy3}`, borderTop: `4px solid ${colors.green}`, borderRadius: '12px', padding: '40px 32px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
          <div style={{ fontFamily: fonts.display, fontSize: '28px', letterSpacing: '1.5px', color: colors.white, marginBottom: '12px' }}>
            Already Activated
          </div>
          <div style={{ fontSize: '14px', color: colors.off, lineHeight: 1.6, marginBottom: '24px' }}>
            Your invitation to <strong style={{ color: colors.gold2 }}>{invitation?.brokerageName}</strong> has already been accepted. Sign in to access your dashboard.
          </div>
          <button
            onClick={() => navigate('/agent-portal/login')}
            style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, border: 'none', borderRadius: '8px', padding: '14px 28px', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', cursor: 'pointer', fontFamily: fonts.body, boxShadow: '0 8px 24px rgba(201,149,42,0.35)' }}
          >
            Sign In →
          </button>
        </div>
      </div>
    );
  }

  // ---- Success state — show invitation details + plan info -----------------
  const planInfo = planLabels[invitation?.plan || 'essential'] || planLabels.essential;
  const showPrice = !invitation?.isSponsorPaid && invitation?.agentMonthlyFee && invitation.agentMonthlyFee > 0;

  return (
    <div style={{ minHeight: '100vh', background: colors.navy, fontFamily: fonts.body, color: colors.off, padding: '40px 20px' }}>
      <link href={FONT_LINK} rel="stylesheet" />
      <div style={{ maxWidth: '760px', margin: '0 auto', background: colors.navy2, border: `1px solid ${colors.navy3}`, borderTop: `4px solid ${colors.gold}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>

        {/* Brand header */}
        <div style={{ textAlign: 'center', padding: '40px 32px 32px', borderBottom: `1px solid ${colors.navy3}` }}>
          <div style={{ fontFamily: fonts.display, fontSize: '32px', letterSpacing: '3px', color: colors.white, marginBottom: '6px' }}>
            AI<span style={{ color: colors.gold2 }}>Search</span>Portal
          </div>
          <div style={{ color: colors.muted, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '32px' }}>
            KairosIQ AI, Inc.
          </div>

          {/* Gold check */}
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2}, ${colors.gold3})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto', fontSize: '36px', color: colors.navy, fontWeight: 900, boxShadow: '0 8px 24px rgba(201,149,42,0.35)' }}>
            ✓
          </div>

          <h1 style={{ fontFamily: fonts.display, fontSize: '38px', letterSpacing: '2px', color: colors.white, margin: '0 0 8px', lineHeight: 1.15 }}>
            You're Invited to<br />
            <span style={{ color: colors.gold2 }}>{invitation?.brokerageName || 'Your Brokerage'}</span>
          </h1>
          {invitation?.agentFirstName && (
            <p style={{ fontSize: '15px', color: colors.muted, margin: '8px 0 0' }}>
              Hi {invitation.agentFirstName}, your workspace is ready to be activated
            </p>
          )}
        </div>

        {/* Plan Details */}
        <div style={{ padding: '36px 32px 24px' }}>

          {/* Subscription Plan Card */}
          <div style={{ background: colors.navy3, borderRadius: '10px', padding: '24px', marginBottom: '28px', border: `1px solid ${colors.gold}33` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <div style={{ fontFamily: fonts.display, fontSize: '22px', letterSpacing: '1.5px', color: colors.gold2, textTransform: 'uppercase' }}>
                  {planInfo.name} Plan
                </div>
                <div style={{ fontSize: '12px', color: colors.muted, marginTop: '4px' }}>
                  {invitation?.isSponsorPaid
                    ? `Sponsored by ${invitation.brokerageName} — no cost to you`
                    : invitation?.sponsorSubsidyPct && invitation.sponsorSubsidyPct > 0
                      ? `${invitation.sponsorSubsidyPct}% sponsored by ${invitation.brokerageName}`
                      : 'Individual subscription'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {invitation?.isSponsorPaid ? (
                  <div>
                    <div style={{ fontFamily: fonts.display, fontSize: '36px', color: colors.green, letterSpacing: '1px' }}>FREE</div>
                    <div style={{ fontSize: '11px', color: colors.muted }}>Fully sponsored</div>
                  </div>
                ) : showPrice ? (
                  <div>
                    <div style={{ fontFamily: fonts.display, fontSize: '36px', color: colors.white, letterSpacing: '1px' }}>
                      ${invitation?.agentMonthlyFee}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.muted }}>per month</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontFamily: fonts.display, fontSize: '36px', color: colors.gold2, letterSpacing: '1px' }}>{planInfo.price}</div>
                    <div style={{ fontSize: '11px', color: colors.muted }}>14-day free trial</div>
                  </div>
                )}
              </div>
            </div>

            {/* Features list */}
            <div style={{ borderTop: `1px solid ${colors.navy}`, paddingTop: '16px' }}>
              {planInfo.features.map((feature, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
                  <span style={{ color: colors.gold2, fontSize: '14px' }}>✓</span>
                  <span style={{ fontSize: '13px', color: colors.off }}>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* What you get */}
          <div style={{ fontFamily: fonts.display, fontSize: '18px', letterSpacing: '1.5px', color: colors.gold2, textTransform: 'uppercase', marginBottom: '20px' }}>
            What you'll get access to
          </div>

          <Feature title="AI-Powered MLS Search" desc="Your buyers get their own AI-powered search portal — you close the deal" />
          <Feature title="Smart CMA Reports" desc="AI-powered comparative market analysis in 35 seconds with price prediction" />
          <Feature title="Full CRM System" desc="Lead management, deal pipeline, Gmail/Outlook integration, and automated follow-ups" />
          <Feature title="Team Collaboration" desc="Interoffice communication, lead sharing, and shared resource library" />

          {/* CTA section */}
          <div style={{ marginTop: '36px', padding: '24px', background: `${colors.gold}08`, border: `1px solid ${colors.gold}33`, borderRadius: '10px', textAlign: 'center' }}>
            <div style={{ fontFamily: fonts.display, fontSize: '22px', letterSpacing: '1.5px', color: colors.white, marginBottom: '6px' }}>
              Ready to get started?
            </div>
            <div style={{ fontSize: '13px', color: colors.muted, marginBottom: '20px', lineHeight: 1.5 }}>
              {invitation?.userExists
                ? 'Sign in with your existing account to activate your invitation.'
                : 'Create your account to activate your invitation and start using the platform.'}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {invitation?.userExists ? (
                <button onClick={handleLogin} style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2}, ${colors.gold3})`, color: colors.navy, border: 'none', borderRadius: '8px', padding: '14px 28px', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', cursor: 'pointer', fontFamily: fonts.body, boxShadow: '0 8px 24px rgba(201,149,42,0.35)' }}>
                  Sign In to Activate →
                </button>
              ) : (
                <>
                  <button onClick={handleSignUp} style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2}, ${colors.gold3})`, color: colors.navy, border: 'none', borderRadius: '8px', padding: '14px 28px', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', cursor: 'pointer', fontFamily: fonts.body, boxShadow: '0 8px 24px rgba(201,149,42,0.35)' }}>
                    Create Account →
                  </button>
                  <button onClick={handleLogin} style={{ background: 'transparent', color: colors.gold2, border: `1px solid ${colors.gold}`, borderRadius: '8px', padding: '14px 28px', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', cursor: 'pointer', fontFamily: fonts.body }}>
                    Already have an account? Sign In
                  </button>
                </>
              )}
            </div>
          </div>

          <div style={{ marginTop: '28px', textAlign: 'center', fontSize: '11px', color: colors.muted, lineHeight: 1.6 }}>
            🔒 Secure, compliant, and built for real estate professionals
            <br />
            Questions? Contact <a href="mailto:support@kairosiq.ai" style={{ color: colors.gold2, textDecoration: 'none' }}>support@kairosiq.ai</a>
          </div>
        </div>

        {/* Footer */}
        <div style={{ background: colors.navy, padding: '24px 32px', textAlign: 'center', borderTop: `1px solid ${colors.navy3}` }}>
          <div style={{ fontFamily: fonts.display, fontSize: '16px', letterSpacing: '2px', color: colors.white, marginBottom: '4px' }}>
            KAIROS IQ AI
          </div>
          <div style={{ fontSize: '11px', color: colors.muted }}>
            © 2026 KairosIQ AI. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

const Feature: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 0', borderBottom: `1px solid ${colors.navy3}` }}>
    <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: 'rgba(201,149,42,0.15)', border: '1px solid rgba(201,149,42,0.5)', color: colors.gold2, fontSize: '16px', fontWeight: 700, textAlign: 'center', lineHeight: '30px', flexShrink: 0 }}>
      ✦
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: colors.white, fontSize: '14px', fontWeight: 600, marginBottom: '3px' }}>{title}</div>
      <div style={{ color: colors.muted, fontSize: '13px', lineHeight: 1.5 }}>{desc}</div>
    </div>
  </div>
);

export default AcceptInvitationAIForward;
